import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Stepper, Step, StepLabel, StepButton, Typography, Button,
  TextField, MenuItem, Grid, Paper, Chip, IconButton, Divider,
  Alert, CircularProgress, Stack, FormControlLabel, Switch,
  Card, CardContent, CardActionArea, Collapse, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Badge, InputAdornment,
  Tabs, Tab, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Business, CreditCard, Route, Security, Person, CheckCircle,
  Add, Delete, ArrowBack, ArrowForward, AutoAwesome, Inventory2,
  AccountBalance, SkipNext, Refresh, Done, ExpandMore, ExpandLess,
  VpnKey, Schedule, Phone, Headset, Router, PhoneInTalk,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../../services/api';

// Service types that drive step visibility and smart defaults
const SERVICE_TYPES = [
  { value: 'voip',    label: 'VoIP / Minutes',    icon: <Phone />,       desc: 'Per-minute calling with rate cards and routing' },
  { value: 'pbx',     label: 'PBX / UC',           icon: <Router />,      desc: 'PBX, extensions, hosted telephony — no rate card needed' },
  { value: 'cc',      label: 'Contact Centre',      icon: <Headset />,     desc: 'Agents, queues, bots — no rate card needed' },
  { value: 'full',    label: 'Full Service',         icon: <PhoneInTalk />, desc: 'Minutes + PBX + Contact Centre combined' },
];

const STEPS = [
  { label: 'Customer Profile' },
  { label: 'Billing & Payment' },
  { label: 'Rate Plan', optional: true },
  { label: 'Routing', optional: true },
  { label: 'Products & Subscriptions', optional: true },
  { label: 'SIP Access', optional: true },
  { label: 'Portal User', optional: true },
  { label: 'Review & Confirm' },
];

// Steps where rate plan / routing aren't relevant
const NO_MINUTES_TYPES = new Set(['pbx', 'cc']);

// Generates a random binder ID: 3 uppercase letters + 6 digits, e.g. KTX948271
// Used as the universal account code that ties all services (SIM, PBX, CDR, billing) together
function generateBinderId(): string {
  const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // no I/O to avoid confusion
  const digits  = '0123456789';
  const alpha = Array.from({ length: 3 }, () => letters[Math.floor(Math.random() * letters.length)]).join('');
  const num   = Array.from({ length: 6 }, () => digits[Math.floor(Math.random() * digits.length)]).join('');
  return alpha + num;
}

const defaultForm = {
  name: '', code: generateBinderId(), company_name: '', tier: 'customer', parent_id: '',
  contact_email: '', contact_phone: '', city: '',
  service_type: '',
  billing_type: 'postpaid', currency: 'AED', credit_limit: 0,
  invoice_period: 'monthly', payment_method: 'bank_transfer', payment_terms: 30,
  billing_email: '', billing_contact_name: '', billing_phone: '', tax_id: '',
  rate_plan_id: '', routing_profile_id: '',
  trunk_host: '', trunk_port: 5060, trunk_protocol: 'UDP', trunk_auth: 'ip', trunk_username: '', trunk_password: '', trunk_srtp: 'none',
  sip_device: false, device_caller_number: '', device_voicemail: false,
  create_user: false, user_email: '', user_first_name: '', user_last_name: '',
  user_password: '', user_role: 'customer_admin',
};

function generatePwd(): string {
  const a = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
  return Array.from({ length: 12 }, () => a[Math.floor(Math.random() * a.length)]).join('');
}

export function CustomerOnboardingPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [step, setStep] = useState(0);
  const [completed, setCompleted] = useState<Set<number>>(new Set());
  const [form, setForm] = useState<any>({ ...defaultForm });
  const [errors, setErrors] = useState<any>({});
  const [subs, setSubs] = useState<any[]>([]);
  const [aclRows, setAclRows] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<any>(null);

  // Reference data
  const [resellers, setResellers] = useState<any[]>([]);
  const [ratePlans, setRatePlans] = useState<any[]>([]);
  const [routingProfiles, setRoutingProfiles] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [skus, setSkus] = useState<any[]>([]);
  const [loadingRef, setLoadingRef] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);
  const [productCategoryTab, setProductCategoryTab] = useState<string>('all');

  const isNoMinutes = NO_MINUTES_TYPES.has(form.service_type);

  useEffect(() => {
    setLoadingRef(true);
    Promise.all([
      api.get('/tenants?tier=reseller&limit=100'),
      api.get('/rates/plans?limit=100'),
      api.get('/routing/profiles'),
      api.get('/subscriptions/products'),
      api.get('/subscriptions/skus'),
      api.get('/subscriptions/categories'),
    ]).then(([res, rp, routing, prods, sk, cats]) => {
      setResellers(res.data?.data || res.data || []);
      setRatePlans(rp.data?.data || rp.data || []);
      setRoutingProfiles(routing.data?.data || routing.data || []);
      setProducts(prods.data || []);
      setSkus(sk.data || []);
      setCategories(cats.data || []);
    }).catch(() => {}).finally(() => setLoadingRef(false));
  }, []);

  // Auto-set product category tab when entering the products step based on service type
  useEffect(() => {
    if (step === 4) {
      if (form.service_type === 'pbx' || form.service_type === 'cc') {
        const pbxCat = categories.find((c: any) => c.name?.toLowerCase().includes('pbx') || c.name?.toLowerCase().includes('contact'));
        if (pbxCat) setProductCategoryTab(pbxCat.id);
      } else if (form.service_type === 'voip') {
        const minCat = categories.find((c: any) => c.name?.toLowerCase().includes('minute'));
        if (minCat) setProductCategoryTab(minCat.id);
      }
    }
  }, [step, form.service_type, categories]);

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const validateStep = (s: number): boolean => {
    const e: any = {};
    if (s === 0) {
      if (!form.name.trim()) e.name = 'Required';
      if (!form.code.trim()) e.code = 'Required';
      if (!form.contact_email.trim()) e.contact_email = 'Required';
    }
    if (s === 1) {
      if (!form.billing_type) e.billing_type = 'Required';
      if (!form.currency) e.currency = 'Required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep(step)) return;
    setCompleted(prev => new Set([...prev, step]));
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep(s => Math.max(s - 1, 0));
  const skip = () => { setCompleted(prev => new Set([...prev, step])); setStep(s => s + 1); };

  const addSub = (sku: any, productName: string) => {
    if (subs.find(s => s.sku_id === sku.id)) return;
    setSubs(prev => [...prev, {
      sku_id: sku.id, sku_name: sku.name, product_name: productName,
      quantity: 1, price: parseFloat(sku.default_price || 0), currency: sku.currency || 'AED',
      billing_day: 1, notes: '',
    }]);
  };

  const removeSub = (sku_id: string) => setSubs(prev => prev.filter(s => s.sku_id !== sku_id));
  const updateSub = (sku_id: string, k: string, v: any) =>
    setSubs(prev => prev.map(s => s.sku_id === sku_id ? { ...s, [k]: v } : s));

  const addAcl = () => setAclRows(prev => [...prev, { ip_addr: '', mask: 32, port: 0, description: '', _id: Date.now() }]);
  const removeAcl = (id: number) => setAclRows(prev => prev.filter(a => a._id !== id));
  const updateAcl = (id: number, k: string, v: any) =>
    setAclRows(prev => prev.map(a => a._id === id ? { ...a, [k]: v } : a));

  const skusFor = (productId: string) => skus.filter((s: any) => s.product_id === productId);

  const filteredProducts = productCategoryTab === 'all'
    ? products
    : products.filter((p: any) => p.category_id === productCategoryTab);

  const submit = async () => {
    if (!validateStep(0) || !validateStep(1)) { setStep(0); return; }
    setSubmitting(true);
    try {
      const payload: any = {
        name: form.name, code: form.code, company_name: form.company_name || form.name,
        tier: form.tier, parent_id: form.parent_id || undefined,
        contact_email: form.contact_email, contact_phone: form.contact_phone || undefined,
        city: form.city || undefined,
        billing_type: form.billing_type, currency: form.currency,
        credit_limit: Number(form.credit_limit) || 0,
        invoice_period: form.invoice_period, payment_method: form.payment_method,
        payment_terms: Number(form.payment_terms),
        billing_email: form.billing_email || undefined,
        billing_contact_name: form.billing_contact_name || undefined,
        billing_phone: form.billing_phone || undefined,
        tax_id: form.tax_id || undefined,
        rate_plan_id: form.rate_plan_id || undefined,
        routing_profile_id: form.routing_profile_id || undefined,
        subscriptions: subs.map(s => ({
          sku_id: s.sku_id, quantity: Number(s.quantity) || 1,
          price: Number(s.price), currency: s.currency || 'AED',
          billing_day: Number(s.billing_day) || 1, notes: s.notes || undefined,
        })),
        acl_entries: aclRows.filter(a => a.ip_addr).map(a => ({
          ip_addr: a.ip_addr, mask: Number(a.mask) || 32,
          port: Number(a.port) || 0, description: a.description || undefined,
        })),
        trunk: form.trunk_host ? {
          host: form.trunk_host, port: Number(form.trunk_port) || 5060,
          protocol: form.trunk_protocol || 'UDP', auth_type: form.trunk_auth || 'ip',
          username: form.trunk_auth === 'userpass' ? form.trunk_username : undefined,
          password: form.trunk_auth === 'userpass' ? form.trunk_password : undefined,
          srtp: form.trunk_srtp || 'none',
        } : undefined,
        sip_device: !!form.sip_device,
        device_caller_number: form.sip_device ? (form.device_caller_number || undefined) : undefined,
        device_voicemail: form.sip_device ? !!form.device_voicemail : undefined,
        create_user: form.create_user,
        user_email: form.create_user ? form.user_email : undefined,
        user_first_name: form.create_user ? form.user_first_name : undefined,
        user_last_name: form.create_user ? form.user_last_name : undefined,
        user_password: form.create_user ? form.user_password : undefined,
        user_role: form.create_user ? form.user_role : undefined,
      };
      const { data } = await api.post('/tenants/onboard', payload);
      setSuccess(data);
      enqueueSnackbar('Customer created successfully!', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to create customer', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step renderers ──────────────────────────────────────────────────────

  const renderStep0 = () => (
    <Grid container spacing={2}>
      {/* Service type quick-pick */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>Service Type</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Select the type of service to streamline the onboarding steps
        </Typography>
        <Grid container spacing={1.5}>
          {SERVICE_TYPES.map(st => (
            <Grid item xs={12} sm={6} md={3} key={st.value}>
              <Paper
                variant="outlined"
                onClick={() => set('service_type', form.service_type === st.value ? '' : st.value)}
                sx={{
                  p: 2, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
                  borderColor: form.service_type === st.value ? 'primary.main' : 'divider',
                  bgcolor: form.service_type === st.value ? 'primary.50' : 'background.paper',
                  '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                }}
              >
                <Box sx={{ color: form.service_type === st.value ? 'primary.main' : 'text.secondary', mb: 0.5 }}>
                  {st.icon}
                </Box>
                <Typography variant="subtitle2" fontWeight={form.service_type === st.value ? 700 : 400}>
                  {st.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">{st.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
        {isNoMinutes && (
          <Alert severity="info" icon={<CheckCircle />} sx={{ mt: 1.5 }}>
            <strong>{form.service_type === 'pbx' ? 'PBX / UC' : 'Contact Centre'} customer</strong> — Rate Plan and Routing steps will be skipped automatically. Only product subscriptions are needed.
          </Alert>
        )}
      </Grid>

      <Grid item xs={12}><Divider /></Grid>

      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Company / Customer Name *" value={form.name}
          onChange={e => set('name', e.target.value)}
          error={!!errors.name} helperText={errors.name} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Binder ID (Account Code) *" value={form.code}
          onChange={e => set('code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
          error={!!errors.code}
          helperText={errors.code || 'Auto-generated unique ID — ties all services (SIM, PBX, CDR, billing) together'}
          InputProps={{ endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Generate new binder ID">
                <IconButton size="small" onClick={() => set('code', generateBinderId())}>
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          )}} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Company Name (for invoices)" value={form.company_name}
          onChange={e => set('company_name', e.target.value)}
          helperText="Defaults to customer name if blank" />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth select label="Account Tier" value={form.tier}
          onChange={e => set('tier', e.target.value)}>
          <MenuItem value="customer">Customer</MenuItem>
          <MenuItem value="reseller">Reseller</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth select label="Parent / Reseller (optional)" value={form.parent_id}
          onChange={e => set('parent_id', e.target.value)}>
          <MenuItem value="">— None —</MenuItem>
          {resellers.map((r: any) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Contact Email *" type="email" value={form.contact_email}
          onChange={e => set('contact_email', e.target.value)}
          error={!!errors.contact_email} helperText={errors.contact_email} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Contact Phone" value={form.contact_phone}
          onChange={e => set('contact_phone', e.target.value)} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="City" value={form.city}
          onChange={e => set('city', e.target.value)} />
      </Grid>
    </Grid>
  );

  const renderStep1 = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField fullWidth select label="Billing Type *" value={form.billing_type}
          onChange={e => set('billing_type', e.target.value)}
          error={!!errors.billing_type} helperText={errors.billing_type}>
          {['prepaid','postpaid','wholesale','retail','enterprise','reseller'].map(t =>
            <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth select label="Currency *" value={form.currency}
          onChange={e => set('currency', e.target.value)}
          error={!!errors.currency} helperText={errors.currency}>
          {['AED','USD','GBP','EUR'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
      </Grid>
      {['postpaid','wholesale','retail','enterprise'].includes(form.billing_type) && (
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Credit Limit" type="number" value={form.credit_limit}
            onChange={e => set('credit_limit', e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">{form.currency}</InputAdornment> }} />
        </Grid>
      )}
      <Grid item xs={12}><Divider><Chip label="Invoicing Settings" size="small" /></Divider></Grid>
      <Grid item xs={12} md={4}>
        <TextField fullWidth select label="Billing Period" value={form.invoice_period}
          onChange={e => set('invoice_period', e.target.value)}>
          <MenuItem value="weekly">Weekly</MenuItem>
          <MenuItem value="biweekly">Bi-weekly</MenuItem>
          <MenuItem value="monthly">Monthly</MenuItem>
          <MenuItem value="quarterly">Quarterly</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField fullWidth select label="Payment Terms (days)" value={form.payment_terms}
          onChange={e => set('payment_terms', Number(e.target.value))}>
          <MenuItem value={15}>Net 15</MenuItem>
          <MenuItem value={30}>Net 30</MenuItem>
          <MenuItem value={45}>Net 45</MenuItem>
          <MenuItem value={60}>Net 60</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField fullWidth select label="Payment Method" value={form.payment_method}
          onChange={e => set('payment_method', e.target.value)}>
          <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
          <MenuItem value="credit_card">Credit Card</MenuItem>
          <MenuItem value="online">Online Payment</MenuItem>
          <MenuItem value="wire">Wire Transfer</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12}><Divider><Chip label="Billing Contact" size="small" /></Divider></Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Billing Email" type="email" value={form.billing_email}
          onChange={e => set('billing_email', e.target.value)}
          helperText="Where invoices & billing emails go (defaults to Contact Email)" />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Billing Contact Name" value={form.billing_contact_name}
          onChange={e => set('billing_contact_name', e.target.value)} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Billing Phone" value={form.billing_phone}
          onChange={e => set('billing_phone', e.target.value)} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Tax ID / TRN / VAT" value={form.tax_id}
          onChange={e => set('tax_id', e.target.value)} />
      </Grid>
    </Grid>
  );

  const renderStep2 = () => (
    <Box>
      {isNoMinutes ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Not required for {form.service_type === 'pbx' ? 'PBX / UC' : 'Contact Centre'} customers.</strong>
          {' '}Rate plans are only needed for customers using per-minute VoIP calling. You can skip this step.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Rate plans define per-destination sell rates. You can skip this and assign from the customer page later.
        </Alert>
      )}
      <TextField fullWidth select label="Rate Plan" value={form.rate_plan_id}
        onChange={e => set('rate_plan_id', e.target.value)}>
        <MenuItem value="">— No rate plan (assign later) —</MenuItem>
        {ratePlans.map((p: any) => (
          <MenuItem key={p.id} value={p.id}>{p.name} ({p.plan_type} · {p.currency})</MenuItem>
        ))}
      </TextField>
    </Box>
  );

  const renderStep3 = () => (
    <Box>
      {isNoMinutes ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Not required for {form.service_type === 'pbx' ? 'PBX / UC' : 'Contact Centre'} customers.</strong>
          {' '}Routing profiles control SIP call routing to carriers — not needed without VoIP minutes. You can skip this step.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Routing profiles control how calls from this customer are sent to carriers.
        </Alert>
      )}
      <TextField fullWidth select label="Routing Profile" value={form.routing_profile_id}
        onChange={e => set('routing_profile_id', e.target.value)}>
        <MenuItem value="">— Use platform default —</MenuItem>
        {routingProfiles.map((p: any) => (
          <MenuItem key={p.id} value={p.id}>{p.name} ({p.strategy})</MenuItem>
        ))}
      </TextField>

      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Customer SIP Trunk (ingress)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The customer's SIP device / gateway that connects into the SBC. An IP-based trunk is auto-trusted in Kamailio permissions and the SIP firewall. Optional — you can also add it later from the customer page.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={8}><TextField fullWidth label="Trunk host / IP" value={form.trunk_host} onChange={e => set('trunk_host', e.target.value)} placeholder="e.g. 151.253.188.4" /></Grid>
        <Grid item xs={6} sm={4}><TextField fullWidth type="number" label="Port" value={form.trunk_port} onChange={e => set('trunk_port', e.target.value)} /></Grid>
        <Grid item xs={6} sm={4}>
          <TextField fullWidth select label="Transport" value={form.trunk_protocol} onChange={e => set('trunk_protocol', e.target.value)}>
            <MenuItem value="UDP">UDP</MenuItem><MenuItem value="TCP">TCP</MenuItem><MenuItem value="TLS">TLS</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={6} sm={4}>
          <TextField fullWidth select label="Authentication" value={form.trunk_auth} onChange={e => set('trunk_auth', e.target.value)}>
            <MenuItem value="ip">IP-based</MenuItem><MenuItem value="userpass">Username / Password</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={6} sm={4}>
          <TextField fullWidth select label="Media (SRTP)" value={form.trunk_srtp} onChange={e => set('trunk_srtp', e.target.value)}>
            <MenuItem value="none">Plain RTP</MenuItem><MenuItem value="optional">SRTP optional</MenuItem><MenuItem value="require">SRTP required</MenuItem>
          </TextField>
        </Grid>
        {form.trunk_auth === 'userpass' && <>
          <Grid item xs={6}><TextField fullWidth label="Username" value={form.trunk_username} onChange={e => set('trunk_username', e.target.value)} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Password" type="password" value={form.trunk_password} onChange={e => set('trunk_password', e.target.value)} /></Grid>
        </>}
      </Grid>

      <Divider sx={{ my: 3 }} />
      <FormControlLabel
        control={<Switch checked={form.sip_device} onChange={e => set('sip_device', e.target.checked)} />}
        label={<Typography variant="subtitle2" fontWeight={700}>SIP Device (registers to our SBC)</Typography>}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Instead of (or as well as) an IP trunk, issue a SIP account. The customer's softphone/PBX registers to <b>esbc2.synergytalk.org:5080 (TLS)</b> with an auto-generated username &amp; password — no fixed IP needed. Inbound DIDs are delivered to the registered device.
      </Typography>
      {form.sip_device && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={7}><TextField fullWidth label="Device caller number (outbound CLI)" value={form.device_caller_number} onChange={e => set('device_caller_number', e.target.value)} placeholder="e.g. +442046131900" /></Grid>
          <Grid item xs={12} sm={5}><FormControlLabel control={<Switch checked={form.device_voicemail} onChange={e => set('device_voicemail', e.target.checked)} />} label="Voicemail" /></Grid>
        </Grid>
      )}
    </Box>
  );

  const renderStep4 = () => {
    // Build category list with "All" + fetched categories, fall back to deriving from products
    const derivedCats: any[] = categories.length > 0
      ? categories
      : Array.from(new Map(products.map((p: any) => [p.category_id, { id: p.category_id, name: p.category_name || p.category_id }])).values());

    const productSubsCount = subs.length;
    const monthlyTotal = subs.reduce((sum, s) => sum + (parseFloat(s.price) * (s.quantity || 1)), 0);

    return (
      <Box>
        {/* Header summary bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Product Catalogue</Typography>
            <Typography variant="caption" color="text.secondary">
              Select products to subscribe this customer to. Rate, quantity and price can be adjusted per subscription.
            </Typography>
          </Box>
          {productSubsCount > 0 && (
            <Chip
              icon={<CheckCircle />}
              label={`${productSubsCount} selected · ${subs[0]?.currency || 'AED'} ${monthlyTotal.toFixed(2)}/mo`}
              color="success"
              variant="outlined"
            />
          )}
        </Box>

        {/* Category tabs */}
        {derivedCats.length > 0 && (
          <Tabs
            value={productCategoryTab}
            onChange={(_, v) => { setProductCategoryTab(v); setExpandedProduct(null); }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label={`All (${products.length})`} value="all" />
            {derivedCats.map((cat: any) => {
              const count = products.filter((p: any) => p.category_id === cat.id).length;
              const subCount = subs.filter(s =>
                products.filter((p: any) => p.category_id === cat.id).some(p => skusFor(p.id).some(sk => sk.id === s.sku_id))
              ).length;
              return (
                <Tab
                  key={cat.id}
                  value={cat.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {cat.name} ({count})
                      {subCount > 0 && <Chip label={subCount} size="small" color="success" sx={{ height: 18, fontSize: 10 }} />}
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
        )}

        {/* Product grid */}
        {filteredProducts.length === 0 ? (
          <Typography color="text.secondary">No products in this category.</Typography>
        ) : (
          <Grid container spacing={2}>
            {filteredProducts.map((p: any) => {
              const pSkus = skusFor(p.id);
              const isExpanded = expandedProduct === p.id;
              const hasSelected = subs.some(s => pSkus.some(sk => sk.id === s.sku_id));
              const selectedCount = subs.filter(s => pSkus.some(sk => sk.id === s.sku_id)).length;
              return (
                <Grid item xs={12} md={6} key={p.id}>
                  <Paper variant="outlined" sx={{ overflow: 'hidden', borderColor: hasSelected ? 'success.main' : 'divider' }}>
                    <Box
                      sx={{
                        p: 1.5, cursor: 'pointer', display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: hasSelected ? 'success.50' : 'background.paper',
                        '&:hover': { bgcolor: hasSelected ? 'success.50' : 'action.hover' },
                      }}
                      onClick={() => setExpandedProduct(isExpanded ? null : p.id)}
                    >
                      <Box>
                        <Typography variant="subtitle2">{p.name}</Typography>
                        {p.description && (
                          <Typography variant="caption" color="text.secondary">{p.description}</Typography>
                        )}
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5 }}>
                          <Chip label={p.product_type?.replace(/_/g, ' ')} size="small" variant="outlined" />
                          {pSkus.length > 0 && (
                            <Chip
                              label={`from ${pSkus[0]?.currency} ${parseFloat(pSkus[0]?.default_price || 0).toFixed(0)}`}
                              size="small" color="primary" variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {hasSelected && (
                          <Chip label={`${selectedCount} added`} size="small" color="success" />
                        )}
                        {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </Box>
                    </Box>
                    <Collapse in={isExpanded}>
                      <Divider />
                      <Box sx={{ p: 1.5 }}>
                        {pSkus.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">No pricing configured for this product yet.</Typography>
                        ) : pSkus.map((sku: any) => {
                          const selected = subs.find(s => s.sku_id === sku.id);
                          return (
                            <Box key={sku.id} sx={{ mb: 1.5, p: 1, bgcolor: selected ? 'success.50' : 'grey.50', borderRadius: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>{sku.name}</Typography>
                                  {sku.destination && (
                                    <Chip label={sku.destination} size="small" sx={{ mr: 0.5 }} />
                                  )}
                                  {sku.bundle_minutes && (
                                    <Chip label={`${sku.bundle_minutes} mins incl.`} size="small" color="info" sx={{ mr: 0.5 }} />
                                  )}
                                  {parseFloat(sku.overage_rate) > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      Overage: {sku.currency} {parseFloat(sku.overage_rate).toFixed(4)}/min
                                    </Typography>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body1" color="primary.main" fontWeight={700}>
                                    {sku.currency} {parseFloat(sku.default_price || 0).toFixed(2)}
                                    <Typography component="span" variant="caption" color="text.secondary">/mo</Typography>
                                  </Typography>
                                  {selected ? (
                                    <Button size="small" color="error" variant="contained" disableElevation
                                      onClick={() => removeSub(sku.id)}>Remove</Button>
                                  ) : (
                                    <Button size="small" variant="contained" disableElevation
                                      onClick={() => addSub(sku, p.name)}>Add</Button>
                                  )}
                                </Box>
                              </Box>
                              {selected && (
                                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                  <Grid item xs={4}>
                                    <TextField size="small" label="Qty" type="number" value={selected.quantity}
                                      onChange={e => updateSub(sku.id, 'quantity', e.target.value)}
                                      inputProps={{ min: 1 }} fullWidth />
                                  </Grid>
                                  <Grid item xs={4}>
                                    <TextField size="small" label="Price Override" type="number" value={selected.price}
                                      onChange={e => updateSub(sku.id, 'price', e.target.value)} fullWidth />
                                  </Grid>
                                  <Grid item xs={4}>
                                    <TextField size="small" select label="Currency" value={selected.currency}
                                      onChange={e => updateSub(sku.id, 'currency', e.target.value)} fullWidth>
                                      <MenuItem value="AED">AED</MenuItem>
                                      <MenuItem value="USD">USD</MenuItem>
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <TextField size="small" label="Billing Day (1–28)" type="number" value={selected.billing_day}
                                      onChange={e => updateSub(sku.id, 'billing_day', e.target.value)}
                                      inputProps={{ min: 1, max: 28 }} fullWidth
                                      helperText="Day of month to bill on" />
                                  </Grid>
                                </Grid>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Collapse>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Selected subscriptions summary */}
        {subs.length > 0 && (
          <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              <Inventory2 fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              Selected Subscriptions
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product / SKU</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Total/mo</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {subs.map(s => (
                  <TableRow key={s.sku_id}>
                    <TableCell>{s.product_name} — {s.sku_name}</TableCell>
                    <TableCell align="right">{s.quantity}</TableCell>
                    <TableCell align="right">{s.currency} {parseFloat(s.price).toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {s.currency} {(parseFloat(s.price) * (s.quantity || 1)).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => removeSub(s.sku_id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: 700, border: 0 }}>Monthly Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main', border: 0 }}>
                    {subs[0]?.currency || 'AED'} {monthlyTotal.toFixed(2)}
                  </TableCell>
                  <TableCell sx={{ border: 0 }} />
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>
    );
  };

  const renderStep5 = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Add IP addresses / CIDR ranges that this customer's SIP endpoints will connect from.
        Kamailio ACL will be reloaded after customer creation.
      </Alert>
      <Button startIcon={<Add />} variant="outlined" onClick={addAcl} sx={{ mb: 2 }}>
        Add IP Entry
      </Button>
      {aclRows.length === 0 ? (
        <Typography color="text.secondary">No ACL entries — skip this step if not needed.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>IP Address</TableCell>
              <TableCell>Mask</TableCell>
              <TableCell>Port</TableCell>
              <TableCell>Description</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {aclRows.map(a => (
              <TableRow key={a._id}>
                <TableCell>
                  <TextField size="small" value={a.ip_addr} placeholder="192.168.1.0"
                    onChange={e => updateAcl(a._id, 'ip_addr', e.target.value)} />
                </TableCell>
                <TableCell>
                  <TextField size="small" type="number" value={a.mask}
                    onChange={e => updateAcl(a._id, 'mask', e.target.value)}
                    inputProps={{ min: 1, max: 32 }} sx={{ width: 80 }} />
                </TableCell>
                <TableCell>
                  <TextField size="small" type="number" value={a.port}
                    onChange={e => updateAcl(a._id, 'port', e.target.value)}
                    inputProps={{ min: 0, max: 65535 }} sx={{ width: 100 }}
                    helperText="0 = any" />
                </TableCell>
                <TableCell>
                  <TextField size="small" value={a.description} placeholder="e.g. Office PBX"
                    onChange={e => updateAcl(a._id, 'description', e.target.value)}
                    sx={{ minWidth: 160 }} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" color="error" onClick={() => removeAcl(a._id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );

  const renderStep6 = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Optionally create a portal login so the customer can view their invoices, subscriptions, and usage.
      </Alert>
      <FormControlLabel
        control={<Switch checked={form.create_user} onChange={e => set('create_user', e.target.checked)} />}
        label="Create portal user account"
        sx={{ mb: 2 }}
      />
      {form.create_user && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="First Name" value={form.user_first_name}
              onChange={e => set('user_first_name', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Last Name" value={form.user_last_name}
              onChange={e => set('user_last_name', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Login Email *" type="email" value={form.user_email}
              onChange={e => set('user_email', e.target.value)}
              helperText={!form.user_email && form.contact_email ? `Suggestion: ${form.contact_email}` : ''}
              InputProps={{ endAdornment: form.contact_email && !form.user_email ? (
                <InputAdornment position="end">
                  <Button size="small" onClick={() => set('user_email', form.contact_email)}>Use contact</Button>
                </InputAdornment>
              ) : undefined }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select label="Role" value={form.user_role}
              onChange={e => set('user_role', e.target.value)}>
              <MenuItem value="customer_admin">Customer Admin</MenuItem>
              <MenuItem value="customer">Customer User</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Password" value={form.user_password}
              onChange={e => set('user_password', e.target.value)}
              helperText="Leave blank to auto-generate a secure password"
              InputProps={{ endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Generate password">
                    <IconButton size="small" onClick={() => set('user_password', generatePwd())}>
                      <VpnKey fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )}} />
          </Grid>
        </Grid>
      )}
    </Box>
  );

  const renderReview = () => {
    const rPlan = ratePlans.find((r: any) => r.id === form.rate_plan_id);
    const rProfile = routingProfiles.find((r: any) => r.id === form.routing_profile_id);
    const reseller = resellers.find((r: any) => r.id === form.parent_id);
    const serviceType = SERVICE_TYPES.find(s => s.value === form.service_type);
    const monthlyTotal = subs.reduce((sum, s) => sum + (parseFloat(s.price) * (s.quantity || 1)), 0);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              <Business fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />Customer Profile
            </Typography>
            <Stack spacing={0.5}>
              <Row label="Name" value={form.name} />
              <Row label="Code" value={form.code} />
              {form.company_name && <Row label="Company" value={form.company_name} />}
              <Row label="Tier" value={form.tier} />
              {serviceType && <Row label="Service Type" value={serviceType.label} />}
              {reseller && <Row label="Parent" value={reseller.name} />}
              {form.contact_email && <Row label="Email" value={form.contact_email} />}
              {form.contact_phone && <Row label="Phone" value={form.contact_phone} />}
              {form.city && <Row label="City" value={form.city} />}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              <AccountBalance fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />Billing & Payment
            </Typography>
            <Stack spacing={0.5}>
              <Row label="Billing Type" value={form.billing_type} />
              <Row label="Currency" value={form.currency} />
              {Number(form.credit_limit) > 0 && <Row label="Credit Limit" value={`${form.currency} ${form.credit_limit}`} />}
              <Row label="Invoice Period" value={form.invoice_period} />
              <Row label="Payment Terms" value={`Net ${form.payment_terms}`} />
              <Row label="Payment Method" value={form.payment_method?.replace(/_/g, ' ')} />
            </Stack>
          </Paper>
        </Grid>

        {(!isNoMinutes || form.rate_plan_id || form.routing_profile_id) && (
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                <Schedule fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />VoIP Services
              </Typography>
              <Stack spacing={0.5}>
                <Row label="Rate Plan" value={rPlan ? rPlan.name : '— None —'} />
                <Row label="Routing" value={rProfile ? rProfile.name : 'Platform default'} />
              </Stack>
            </Paper>
          </Grid>
        )}

        {subs.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                <Inventory2 fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Subscriptions ({subs.length})
              </Typography>
              {subs.map(s => (
                <Box key={s.sku_id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{s.product_name} — {s.sku_name} × {s.quantity}</Typography>
                  <Typography variant="body2" color="primary.main" fontWeight={600}>
                    {s.currency} {(parseFloat(s.price) * (s.quantity || 1)).toFixed(2)}/mo
                  </Typography>
                </Box>
              ))}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Monthly Total</Typography>
                <Typography variant="subtitle2" color="primary.main">
                  {subs[0]?.currency || 'AED'} {monthlyTotal.toFixed(2)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}

        {aclRows.filter(a => a.ip_addr).length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                <Security fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                SIP ACL ({aclRows.filter(a => a.ip_addr).length} entries)
              </Typography>
              {aclRows.filter(a => a.ip_addr).map((a, i) => (
                <Typography key={i} variant="body2">
                  {a.ip_addr}/{a.mask}{a.port ? `:${a.port}` : ''}{a.description ? ` — ${a.description}` : ''}
                </Typography>
              ))}
            </Paper>
          </Grid>
        )}

        {form.create_user && form.user_email && (
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                <Person fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />Portal User
              </Typography>
              <Stack spacing={0.5}>
                <Row label="Email" value={form.user_email} />
                <Row label="Role" value={form.user_role?.replace(/_/g, ' ')} />
                {form.user_first_name && <Row label="Name" value={`${form.user_first_name} ${form.user_last_name}`} />}
                <Row label="Password" value={form.user_password ? '(provided)' : '(auto-generated)'} />
              </Stack>
            </Paper>
          </Grid>
        )}
      </Grid>
    );
  };

  const stepContent = [
    renderStep0, renderStep1, renderStep2, renderStep3,
    renderStep4, renderStep5, renderStep6, renderReview,
  ];

  if (success) {
    return (
      <Box sx={{ p: 3, maxWidth: 700, mx: 'auto', mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle color="success" sx={{ fontSize: 72, mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>Customer Created!</Typography>
          <Typography color="text.secondary" gutterBottom>
            {success.name} has been onboarded successfully.
          </Typography>
          {success.generated_password && (
            <Alert severity="warning" sx={{ mt: 2, mb: 2, textAlign: 'left' }}>
              <strong>Generated portal password:</strong> {success.generated_password}
              <br />Save this — it won't be shown again.
            </Alert>
          )}
          {success.sip_device && (
            <Alert severity="success" sx={{ mt: 2, mb: 2, textAlign: 'left' }}>
              <strong>SIP device credentials</strong> (provision the customer's softphone/PBX):
              <Box sx={{ fontFamily: 'monospace', fontSize: 13, mt: 1 }}>
                Username: {success.sip_device.username}<br />
                Password: {success.sip_device.password}<br />
                Domain/Realm: {success.sip_device.domain}<br />
                Registrar: esbc2.synergytalk.org · Transport: TLS · Port: 5080
              </Box>
            </Alert>
          )}
          <Stack spacing={1} sx={{ mt: 2 }}>
            {success.subscriptions_created > 0 && (
              <Chip label={`${success.subscriptions_created} subscription(s) created`} color="success" />)}
            {success.acl_entries_created > 0 && (
              <Chip label={`${success.acl_entries_created} ACL entry(ies) added`} color="info" />)}
            {success.trunk_created && (
              <Chip label="Customer SIP trunk created" color="primary" />)}
            {success.user_created && (
              <Chip label="Portal user created" color="secondary" />)}
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
            <Button variant="contained" onClick={() => navigate('/tenants')}>
              View Customers
            </Button>
            <Button variant="outlined" onClick={() => {
              setSuccess(null); setForm({ ...defaultForm, code: generateBinderId() }); setSubs([]);
              setAclRows([]); setStep(0); setCompleted(new Set());
            }}>
              Onboard Another
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Business color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Customer Onboarding</Typography>
          <Typography variant="body2" color="text.secondary">
            Set up a new customer account with products, routing, and services
          </Typography>
        </Box>
      </Stack>

      <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
        {STEPS.map((s, i) => (
          <Step key={i} completed={completed.has(i)}>
            <StepButton onClick={() => { if (completed.has(i) || i < step) setStep(i); }}>
              <StepLabel optional={s.optional ? <Typography variant="caption">Optional</Typography> : undefined}>
                {s.label}
              </StepLabel>
            </StepButton>
          </Step>
        ))}
      </Stepper>

      <Paper variant="outlined" sx={{ p: 3, minHeight: 300, mb: 3 }}>
        {loadingRef && step >= 2 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          stepContent[step]()
        )}
      </Paper>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button startIcon={<ArrowBack />} onClick={back} disabled={step === 0}>
          Back
        </Button>
        <Stack direction="row" spacing={2}>
          {STEPS[step]?.optional && step < STEPS.length - 1 && (
            <Button startIcon={<SkipNext />} variant="outlined" onClick={skip}>
              Skip
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button endIcon={<ArrowForward />} variant="contained" onClick={next}>
              Next
            </Button>
          ) : (
            <Button
              startIcon={submitting ? <CircularProgress size={18} /> : <Done />}
              variant="contained" color="success" onClick={submit}
              disabled={submitting}
              sx={{ px: 4 }}
            >
              {submitting ? 'Creating…' : 'Create Customer'}
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

function Row({ label, value }: { label: string; value: any }) {
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>{label}:</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}
